import { createColumnHelper } from '@tanstack/react-table';
import cn from 'classnames';
import { useMemo } from 'react';
import { useSuspenseNetworkCount } from '#api/networks.queries.ts';
import { BlockIcon } from '#assets/ReactSvg.ts';
import { StatusDot } from '#components/atoms/icons/StatusDot.tsx';
import { Pill } from '#components/atoms/pill/Pill.tsx';
import { RoleLink } from '#components/patterns/entity-links/role-link/RoleLink.tsx';
import { IpAddressPillList } from '#components/patterns/ip-address-pill-list/IpAddressPillList.tsx';
import { HostActionMenu } from '#components/patterns/menus/host-action-menu/HostActionMenu.tsx';
import { NameCell } from '#components/patterns/table/name-cell/NameCell.tsx';
import { Table } from '#components/patterns/table/Table.tsx';
import { InlineTagsList } from '#components/patterns/tags/InlineTagsList.tsx';
import type { Host } from '#dn-types/host.ts';
import { NetworkName } from '#screens/settings/networks/name/NetworkName.tsx';
import { HOST_KIND_LABELS, hostKind, isInfraHost } from '#util/host.ts';
import { computeHostStatus } from '#util/hostStatus.ts';
import { HostSummaryRow } from './HostSummaryRow.tsx';
import { useDeviceOwnerEmails } from './useDeviceOwnerEmails.ts';
import styles from './HostsTable.module.css';

type Props = {
  data: Host[];
  /** UNIX milliseconds of last update */
  dataUpdatedAt: number;
  isFetching: boolean;
};

const columnHelper = createColumnHelper<Host>();

export function HostsTable({ data, isFetching, dataUpdatedAt }: Props) {
  const networkCount = useSuspenseNetworkCount().data;
  const ownerEmails = useDeviceOwnerEmails(data);

  const columns = useMemo(() => {
    return [
      columnHelper.accessor('name', {
        header: 'Name',
        size: 296,
        cell: (info) => {
          const name = info.getValue();
          const host = info.row.original;
          const status = computeHostStatus(host, new Date(dataUpdatedAt));

          return (
            <div className="flex align-baseline">
              <StatusDot className="shrink-0 mr-8" status={status} />
              <NameCell
                name={name}
                nameTo={`/hosts/${host.id}`}
                secondRow={
                  <HostSummaryRow
                    host={host}
                    dataUpdatedAt={new Date(dataUpdatedAt)}
                    ownerEmail={host.endpointOIDCUserID ? ownerEmails.get(host.endpointOIDCUserID) : undefined}
                  />
                }
              >
                {isInfraHost(host) ?
                  <Pill className="ml-8 shrink-0">{HOST_KIND_LABELS[hostKind(host)]}</Pill>
                : null}
                {host.isBlocked ?
                  <Pill variant="alert" Icon={BlockIcon} className="ml-8 shrink-0">
                    Blocked
                  </Pill>
                : null}
              </NameCell>
            </div>
          );
        },
      }),
      columnHelper.accessor('ipAddresses', {
        header: 'IP',
        size: 180,
        cell(info) {
          return <IpAddressPillList ipAddresses={info.getValue()} />;
        },
        meta: {
          className: styles.HostsTable_ip,
        },
      }),
      columnHelper.accessor('roleID', {
        header: 'Role',
        size: 108,
        cell(info) {
          const roleID = info.getValue();
          if (!roleID) return null;
          return <RoleLink className="inline" roleID={roleID} />;
        },
        meta: {
          truncate: true,
          className: styles.HostsTable_role,
        },
      }),
      columnHelper.accessor('tags', {
        header: 'Tags',
        size: 200,
        cell(info) {
          return <InlineTagsList tagNames={info.getValue()} truncateAfter={1} linkTags={true} />;
        },
        meta: {
          className: styles.HostsTable_tags,
        },
      }),
      columnHelper.accessor('networkID', {
        header: 'Network',
        size: 150,
        cell(info) {
          return <NetworkName networkId={info.getValue()} />;
        },
        meta: {
          truncate: true,
          className: styles.HostsTable_network,
        },
      }),
      columnHelper.display({
        id: 'action-menu',
        header: '',
        size: 34,
        cell(info) {
          const host = info.row.original;
          return (
            <div className="flex justify-end">
              <HostActionMenu
                hostId={host.id}
                isBlocked={host.isBlocked}
                isLighthouse={host.isLighthouse}
                isRelay={host.isRelay}
                isOIDCDevice={Boolean(host.endpointOIDCUserID)}
              />
            </div>
          );
        },
      }),
    ];
  }, [dataUpdatedAt, ownerEmails]);

  return (
    <Table
      data={data}
      columns={columns}
      rowHeight="tall"
      fixedLayout={true}
      className={cn({ 'opacity-dim': isFetching })}
      hiddenColumnIds={networkCount > 1 ? undefined : ['networkID']}
    />
  );
}
